import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const Mark = () => (
  <span className="mark" aria-hidden="true">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F3DC4E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 3c-6 1-11 6-13 13l-2 5" /><path d="M20 3c0 7-5 12-12 13" /><path d="M9 12h5" /></svg>
  </span>
);
const Tick = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="m5 12 5 5 9-10" /></svg>);
const Tk = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="m5 12 5 5 9-10" /></svg>);

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sheet, setSheet] = useState(false);
  const [glass, setGlass] = useState(false);
  const fanRef = useRef(null);
  const grupoRef = useRef(null);
  const typedRef = useRef(null);
  const start = () => navigate(user ? "/studio" : "/auth?mode=signup&next=/studio");
  const login = () => navigate("/auth?mode=login");

  useEffect(() => {
    const onScroll = () => setGlass(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
    }), { threshold: 0.15 });
    document.querySelectorAll("[data-reveal]").forEach((el) => {
      if (!RM) { el.classList.add("is-armed"); io.observe(el); }
    });

    const stats = document.getElementById("stats");
    if (stats) {
      const so = new IntersectionObserver(([e], o) => {
        if (!e.isIntersecting) return; o.disconnect(); stats.classList.add("in");
        if (RM) return;
        document.querySelectorAll(".count").forEach((c) => {
          const to = +c.dataset.to, t0 = performance.now(), d = 1400;
          const step = (t) => { const p = Math.min(1, (t - t0) / d); const v = p === 1 ? 1 : 1 - Math.pow(2, -10 * p); c.textContent = Math.round(to * v); if (p < 1) requestAnimationFrame(step); };
          c.textContent = "0"; requestAnimationFrame(step);
        });
      }, { threshold: 0.4 });
      so.observe(stats);
    }

    let raf = 0, ang = -14, target = null, t0 = 0, vis = true;
    const g = grupoRef.current, fan = fanRef.current;
    const loop = (t) => { raf = 0; if (!vis || !g) return; if (!t0) t0 = t; const osc = Math.sin((t - t0) / 9000 * Math.PI * 2) * 16 - 6; const meta = target ?? osc; ang += (meta - ang) * (target === null ? 0.05 : 0.12); g.style.transform = `rotateY(${ang}deg) rotateX(6deg)`; raf = requestAnimationFrame(loop); };
    if (!RM && g) { raf = requestAnimationFrame(loop); }
    const onMove = (e) => { if (e.pointerType !== "mouse" || !fan) return; const r = fan.getBoundingClientRect(); target = ((e.clientX - r.left) / r.width - 0.5) * 50; };
    const onLeave = () => { target = null; };
    if (fan) { fan.addEventListener("pointermove", onMove); fan.addEventListener("pointerleave", onLeave); }

    let alive = true;
    (async () => {
      const el = typedRef.current; if (!el || RM) return;
      const lines = ["Arriba, la lente giraba con su zumbido de siempre.", "Pero esa noche el reloj se había detenido a las 21:17.", "Nadie más subía al faro desde el invierno."];
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));
      for (let i = 0; alive; i = (i + 1) % lines.length) {
        const s = lines[i];
        for (let k = 0; k <= s.length && alive; k++) { el.textContent = s.slice(0, k); await wait(45); }
        await wait(1800);
        for (let k = s.length; k >= 0 && alive; k--) { el.textContent = s.slice(0, k); await wait(18); }
        await wait(300);
      }
    })();

    return () => { window.removeEventListener("scroll", onScroll); io.disconnect(); if (raf) cancelAnimationFrame(raf); if (fan) { fan.removeEventListener("pointermove", onMove); fan.removeEventListener("pointerleave", onLeave); } alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="lp" id="top">
      <header className={`hdr ${glass ? "glass" : ""}`}>
        <div className="wrap hdr-in">
          <button className="logo" onClick={() => window.scrollTo({ top: 0 })} data-testid="nav-brand-logo"><Mark />Jarvis <em>Editorial</em></button>
          <nav className="nav" aria-label="Principal">
            <a href="#como">Cómo funciona</a><a href="#recibes">Lo que recibes</a><a href="#precios">Precios</a><a href="#faq">Preguntas</a>
          </nav>
          <div className="hdr-cta">
            {user ? (
              <button className="btn btn-solid btn-sm" data-testid="nav-studio-btn" onClick={() => navigate("/studio")}>Mi estudio</button>
            ) : (
              <>
                <button className="btn btn-ghost btn-sm" data-testid="nav-login-btn" onClick={login}>Entrar</button>
                <button className="btn btn-solid btn-sm" data-testid="start-book-nav-btn" onClick={start}>Empezar gratis</button>
              </>
            )}
          </div>
          <button className="burger" aria-label="Abrir menú" onClick={() => setSheet((s) => !s)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
          </button>
        </div>
      </header>
      <div className={`sheet ${sheet ? "on" : ""}`}>
        <a href="#como" onClick={() => setSheet(false)}>Cómo funciona</a>
        <a href="#recibes" onClick={() => setSheet(false)}>Lo que recibes</a>
        <a href="#precios" onClick={() => setSheet(false)}>Precios</a>
        <a href="#faq" onClick={() => setSheet(false)}>Preguntas</a>
        <div className="row"><button className="btn btn-ghost" onClick={login}>Entrar</button><button className="btn btn-solid" onClick={start}>Empezar gratis</button></div>
      </div>

      <main>
        <section className="hero">
          <div className="wrap hero-grid">
            <div>
              <span className="eyebrow">Editorial de autor · en español</span>
              <h1 className="h1">Tu manuscrito, convertido en un libro <span className="it">listo para</span> <span className="it text-metal text-metal-shine">vender.</span></h1>
              <p className="lead">Sube tu texto y recibe en menos de un minuto un informe editorial honesto y un borrador corregido. Cuando estés a gusto, descargas el libro maquetado, con portada y capítulos.</p>
              <div className="hero-actions">
                <button className="btn cta" data-testid="start-book-btn" onClick={start}><span className="cta-halo" aria-hidden="true" />Empezar mi libro gratis <span className="arrow" aria-hidden="true">→</span></button>
                <a className="btn btn-ghost" href="#estudio">Ver el estudio por dentro</a>
              </div>
              <ul className="trust">
                <li><Tick />Pagas solo al final</li><li><Tick />Revisiones sin límite</li><li><Tick />Los derechos son tuyos</li>
              </ul>
            </div>
            <div className="fan" ref={fanRef} aria-hidden="true">
              <div className="fan-grupo" ref={grupoRef}>
                <div className="pg pg-cover" style={{ transform: "translate3d(-84px,18px,-120px)" }}>
                  <span className="c-orn" /><span className="c-ey">NOVELA</span>
                  <div className="c-t">El faro de las <i>horas perdidas</i></div><span className="c-line" /><span className="c-a">Lucía Montero</span>
                </div>
                <div className="pg pg-report" style={{ transform: "translate3d(0,0,0)" }}>
                  <span className="r-ey">INFORME EDITORIAL · REVISIÓN 1</span>
                  <h4>Tu voz es fuerte. El ritmo, mejorable.</h4>
                  <div className="bar"><span>Voz</span><i style={{ "--w": "88%" }} /><b>Fuerte</b></div>
                  <div className="bar"><span>Imágenes</span><i style={{ "--w": "92%" }} /><b>Fuerte</b></div>
                  <div className="bar" style={{ "--c": "#B8792A" }}><span>Ritmo</span><i style={{ "--w": "56%" }} /><b>Mejorable</b></div>
                  <div className="note">Lo que funciona: <mark>«ese mar color plomo viejo»</mark>.</div>
                </div>
                <div className="pg pg-chapter" style={{ transform: "translate3d(84px,-18px,120px)" }}>
                  <div className="ch-ey">CAPÍTULO UNO</div><div className="ch-t">La luz que no se apaga</div><span className="ch-orn" />
                  <p className="ch-p"><span className="dc">A</span>quella noche el mar tenía <mark>el color del plomo viejo</mark>. Elena subió los ciento doce peldaños del faro de Punta Aguda.</p>
                  <p className="ch-p"><span className="typed" ref={typedRef}>Arriba, la lente giraba con su zumbido de siempre.</span></p>
                  <div className="ch-folio"><span>Lucía Montero</span><span>11</span></div>
                </div>
              </div>
              <div className="margin-note float-soft"><b>NOTA DEL INFORME</b>Imagen potente: se queda pegada al lector. Mantenla.</div>
              <span className="fan-hint">mueve el cursor sobre el libro</span>
            </div>
          </div>
        </section>

        <section className="stats" id="stats">
          <div className="wrap stats-grid">
            <div className="stat" style={{ "--i": 0 }}><div className="n"><span className="count" data-to="60">60</span><small>s</small></div><span className="ln" /><p>o menos para tu primer informe editorial</p></div>
            <div className="stat" style={{ "--i": 1 }}><div className="n"><span className="count" data-to="0">0</span><small>€</small></div><span className="ln" /><p>hasta que descargas el libro terminado</p></div>
            <div className="stat" style={{ "--i": 2 }}><div className="n"><span className="count" data-to="49">49</span><small>€</small></div><span className="ln" /><p>pago único, sin suscripciones</p></div>
            <div className="stat" style={{ "--i": 3 }}><div className="n"><span className="count" data-to="100">100</span><small>%</small></div><span className="ln" /><p>de los derechos siguen siendo tuyos</p></div>
          </div>
        </section>

        <section className="sec" id="para">
          <span className="folio">§ 01</span>
          <div className="wrap">
            <span className="eyebrow" data-reveal="rise">Para quién es</span>
            <h2 className="h2" data-reveal="rise">Escribir fue lo difícil.<br /><em>Publicar no debería serlo.</em></h2>
            <p className="lead" data-reveal="rise">Hacemos el trabajo técnico que suele frenar a los autores: corrección, estructura y maquetación. Tú decides el texto; nosotros lo convertimos en libro.</p>
            <div className="cards3">
              {[["TU PRIMER LIBRO", "Novela, relato o poesía", "Tienes el manuscrito y no sabes por dónde empezar. Te damos una lectura editorial clara y el libro listo."],
                ["AUTOPUBLICACIÓN", "Autores que ya publican", "Acorta semanas de maquetación y corrección entre título y título, con acabado profesional."],
                ["EXPERTOS", "Ensayo y guía práctica", "Coaches y docentes que quieren un libro como carta de presentación, con índices y estructura cuidada."]].map(([t, h, p], i) => (
                <article className="card" data-reveal="rise" style={{ "--reveal-delay": `${i * 80}ms` }} key={h}>
                  <span className="tag">{t}</span><h3>{h}</h3><p>{p}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="sec" id="como" style={{ background: "var(--surface)", borderBlock: "1px solid var(--rule)" }}>
          <span className="folio">§ 02</span>
          <div className="wrap">
            <span className="eyebrow" data-reveal="rise">Cómo funciona</span>
            <h2 className="h2" data-reveal="rise">Cuatro pasos, <em>del documento al libro.</em></h2>
            <div className="steps">
              {[["1", "Datos del libro", "Título, autor, género y tu texto en .docx, .pdf o .txt.", "≈ 5 min"],
                ["2", "Informe y borrador", "Analizamos estilo, ritmo y ortografía, y corregimos sin cambiar tu voz.", "Menos de 1 min"],
                ["3", "Revisiones", "Subes versiones corregidas o dejas notas. Cada revisión regenera el informe.", "A tu ritmo · sin límite"],
                ["4", "Entrega final", "Pagas una sola vez y descargas el libro maquetado.", "Pago único · desde 49 €"]].map(([n, h, p, m], i) => (
                <div className="step" data-reveal="rise" style={{ "--reveal-delay": `${i * 90}ms` }} key={n}>
                  <span className="snum">{n}</span><h3>{h}</h3><p>{p}</p><span className="meta">{m}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sec" id="estudio">
          <span className="folio">§ 03</span>
          <div className="wrap">
            <span className="eyebrow" data-reveal="rise">Tu estudio por dentro</span>
            <h2 className="h2" data-reveal="rise">Editamos tu libro <em>como merece.</em></h2>
            <p className="lead" data-reveal="rise">La página a la izquierda, el informe a la derecha, y todos tus libros a mano.</p>
            <div className="window" data-reveal="zoom">
              <div className="win-bar"><span className="dots"><i /><i /><i /></span><span className="url">jarviseditorial.com/estudio/el-faro</span><span style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="live" />Guardado</span></div>
              <div className="win-body">
                <aside className="mini-side">
                  <div className="lbl">MIS LIBROS</div>
                  <a className="on" href="#estudio">El faro de las horas <span>R1</span></a>
                  <a href="#estudio">Cartas a Ítaca <span>PDF</span></a>
                  <a href="#estudio">Guía del huerto <span>R3</span></a>
                </aside>
                <div className="mini-main">
                  <div className="mini-page">
                    <div className="ch-ey">CAPÍTULO UNO</div><div className="ch-t">La luz que no se apaga</div><span className="ch-orn" />
                    <p className="ch-p"><span className="dc">A</span>quella noche el mar tenía <mark>el color del plomo viejo</mark>. Elena subió los ciento doce peldaños del faro de Punta Aguda como cada tarde desde hacía once años.</p>
                    <p className="ch-p">El ritual la sostenía igual que la barandilla de hierro oxidado.</p>
                  </div>
                </div>
                <aside className="mini-report">
                  <h5>INFORME · REVISIÓN 1</h5><div className="score">8,4<small> / 10</small></div><span className="pill">▲ +0,6 desde R0</span>
                  <div style={{ marginTop: 22 }}>
                    <div className="bar"><span>Voz</span><i style={{ "--w": "88%" }} /><b>Fuerte</b></div>
                    <div className="bar"><span>Imágenes</span><i style={{ "--w": "92%" }} /><b>Fuerte</b></div>
                    <div className="bar" style={{ "--c": "#B8792A" }}><span>Ritmo</span><i style={{ "--w": "56%" }} /><b>Mejorable</b></div>
                  </div>
                  <p style={{ font: "400 15px/1.5 var(--serif)", margin: "20px 0 0", color: "var(--muted)" }}>Siguiente paso: desarrolla con calma el despertar del desconocido.</p>
                </aside>
              </div>
            </div>
          </div>
        </section>

        <section className="sec" id="recibes" style={{ background: "var(--surface)", borderBlock: "1px solid var(--rule)" }}>
          <span className="folio">§ 04</span>
          <div className="wrap">
            <span className="eyebrow" data-reveal="rise">Lo que recibes</span>
            <h2 className="h2" data-reveal="rise">Un libro terminado, <em>no un borrador a medias.</em></h2>
            <div className="get">
              {[["Interior maquetado", "PDF de imprenta al tamaño elegido con márgenes y tipografía de libro."],
                ["Portada a medida", "Cubierta coherente con el género y el tono de tu libro."],
                ["Capítulos ordenados", "Títulos, numeración de páginas e índice cuando hace falta."],
                ["Informe editorial", "Fortalezas, puntos a trabajar y próximos pasos en cada revisión."],
                ["Ortografía y estilo", "Erratas, puntuación y repeticiones corregidas respetando tu voz."],
                ["Hojas de cortesía", "Portada interior, página de créditos y contraportada."]].map(([h, p], i) => (
                <div className="get-item" data-reveal="rise" style={{ "--reveal-delay": `${(i % 3) * 80}ms` }} key={h}>
                  <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5" /></svg></div>
                  <h3>{h}</h3><p>{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sec" id="precios">
          <span className="folio">§ 05</span>
          <div className="wrap">
            <div style={{ textAlign: "center" }}>
              <span className="eyebrow" data-reveal="rise">Precios</span>
              <h2 className="h2" data-reveal="rise">Un pago, al final. <em>Sin suscripciones.</em></h2>
              <p className="lead" data-reveal="rise" style={{ marginInline: "auto" }}>El informe, el borrador y las revisiones son gratis. Solo pagas cuando descargas el libro terminado.</p>
            </div>
            <div className="prices">
              <article className="price" data-reveal="rise">
                <h3 className="pname">Estándar</h3><p className="pdesc">Novela, relato o ensayo de texto corrido.</p>
                <div className="amt">49<sup>€</sup></div><div className="once">PAGO ÚNICO · IVA INCLUIDO</div>
                <ul>{["Hasta 150 páginas", "Maquetación clásica", "Informe y revisiones sin límite", "PDF de imprenta y portada a medida"].map((t) => <li key={t}><Tk />{t}</li>)}</ul>
                <button className="btn btn-ghost" onClick={start}>Empezar con Estándar</button>
              </article>
              <article className="price feat" data-reveal="rise" style={{ "--reveal-delay": "100ms" }}>
                <span className="badge">EL MÁS ELEGIDO</span>
                <h3 className="pname">Complejo</h3><p className="pdesc">Libros extensos o con estructura especial.</p>
                <div className="amt">79<sup>€</sup></div><div className="once">PAGO ÚNICO · IVA INCLUIDO</div>
                <ul>{["Más de 150 páginas", "Maquetación avanzada, índice y secciones", "Tablas, notas y ilustraciones", "Todo lo del plan Estándar"].map((t) => <li key={t}><Tk />{t}</li>)}</ul>
                <button className="btn btn-light" onClick={start}>Empezar con Complejo</button>
              </article>
              <article className="price soon" data-reveal="rise" style={{ "--reveal-delay": "200ms" }}>
                <h3 className="pname">Editorial+</h3><p className="pdesc">Con lectura y revisión final de un editor humano.</p>
                <div className="amt">Próximamente</div><div className="once">LISTA DE ESPERA ABIERTA</div>
                <ul>{["Todo lo del plan Complejo", "Revisión final por editor", "Publicación en tiendas y metadatos"].map((t) => <li key={t}><Tk />{t}</li>)}</ul>
                <a className="btn btn-ghost" href="#faq">Avísame cuando esté</a>
              </article>
            </div>
            <div className="pay-note">
              <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>Pago seguro con tarjeta</span>
              <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3 4 7v5c0 5 3.5 8 8 9 4.5-1 8-4 8-9V7z" /></svg>Tu manuscrito no se usa para entrenar IA</span>
            </div>
          </div>
        </section>

        <section className="sec dark aurora" id="autores">
          <div className="wrap">
            <span className="eyebrow" data-reveal="rise">Autores</span>
            <h2 className="h2" data-reveal="rise" style={{ color: "#fff" }}>Lo que dicen quienes <em style={{ color: "var(--marker)" }}>ya tienen su libro.</em></h2>
            <div className="quotes">
              {[["Llevaba tres años con la novela en un cajón. El informe me dijo qué arreglar y en dos semanas tenía el PDF listo.", "AA", "Novela · Estándar"],
                ["Mi guía tenía tablas y notas. Pensé que sería imposible maquetarla sin un diseñador.", "BB", "Guía práctica · Complejo"],
                ["No me reescribió el libro. Me señaló dónde flojeaba y me dejó decidir a mí.", "CC", "Poemario · Estándar"]].map(([q, av, meta], i) => (
                <figure className="quote" data-reveal="rise" style={{ "--reveal-delay": `${i * 100}ms` }} key={av}>
                  <blockquote>{q}</blockquote>
                  <figcaption className="who"><span className="av">{av}</span><span>Nombre del autor<br />{meta}</span></figcaption>
                </figure>
              ))}
            </div>
            <span className="placeholder-flag">⚑ MAQUETA · sustituir por testimonios reales antes de publicar</span>
          </div>
        </section>

        <section className="sec" id="faq">
          <span className="folio">§ 06</span>
          <div className="wrap faq-grid">
            <div>
              <span className="eyebrow" data-reveal="rise">Preguntas frecuentes</span>
              <h2 className="h2" data-reveal="rise">Lo que suelen <em>preguntarnos.</em></h2>
              <div className="contact-card" data-reveal="rise">¿Otra duda? Escríbenos a <a href="mailto:hola@jarviseditorial.com">hola@jarviseditorial.com</a> y te respondemos en menos de 24 h.</div>
            </div>
            <div className="faq" data-reveal="rise">
              {[["¿Los derechos del libro siguen siendo míos?", "Sí. Tú eres el autor y conservas todos los derechos sobre el texto y los archivos. Jarvis Editorial es una herramienta, no una editorial que se quede con tu obra."],
                ["¿Reescribís mi libro?", "No. Corregimos erratas y señalamos lo que se puede mejorar; las decisiones de estilo son siempre tuyas."],
                ["¿Cuándo pago y qué recibo?", "Solo al descargar el libro terminado: PDF de imprenta, portada y hojas de cortesía."],
                ["¿Qué formatos puedo subir?", ".docx (recomendado), .pdf o .txt. También puedes pegar el texto directamente."],
                ["¿Puedo llevarlo a una imprenta o a Amazon?", "Sí, el PDF sale en el tamaño que elijas, un formato aceptado por imprentas y plataformas de autopublicación."],
                ["¿Qué hacéis con mi manuscrito?", "Solo lo usamos para generar tu informe y tu libro. Puedes borrarlo cuando quieras desde tu estudio."]].map(([q, a], i) => (
                <details key={q} open={i === 0}><summary>{q}<span className="pm" aria-hidden="true">+</span></summary><div className="ans">{a}</div></details>
              ))}
            </div>
          </div>
        </section>

        <section className="final aurora" data-reveal="zoom">
          <div>
            <h2>Tu libro está más cerca <em>de lo que crees.</em></h2>
            <p>Sube tu manuscrito y recibe tu informe editorial gratis en menos de un minuto.</p>
          </div>
          <div className="act">
            <button className="btn btn-light" data-testid="start-book-cta-btn" style={{ height: 56, padding: "0 28px", fontSize: 16 }} onClick={start}>Empezar mi libro gratis →</button>
            <small>Sin tarjeta para empezar</small>
          </div>
        </section>
      </main>

      <footer className="ftr">
        <div className="wrap">
          <div className="ftr-grid">
            <div>
              <span className="logo"><Mark />Jarvis <em>Editorial</em></span>
              <p style={{ color: "var(--muted)", fontSize: 15, maxWidth: "30ch", margin: "16px 0 0" }}>Editorial de autor en español. Del manuscrito al libro, sin nada técnico.</p>
            </div>
            <div><h4>Producto</h4><ul><li><a href="#como">Cómo funciona</a></li><li><a href="#precios">Precios</a></li><li><a href="#estudio">El estudio</a></li><li><a href="#faq">Preguntas</a></li></ul></div>
            <div><h4>Ayuda</h4><ul><li><a href="mailto:hola@jarviseditorial.com">Contacto</a></li><li><a href="#faq">Centro de ayuda</a></li><li><button className="logo" style={{ font: "inherit", color: "var(--ink)", padding: 0 }} onClick={login}>Entrar</button></li></ul></div>
            <div><h4>Legal</h4><ul><li><a href="#faq">Aviso legal</a></li><li><a href="#faq">Privacidad</a></li><li><a href="#faq">Términos</a></li></ul></div>
          </div>
          <div className="bottom"><span>© 2026 Jarvis Editorial · Hecho en español</span><span>Jarvis Editorial · en español</span></div>
        </div>
      </footer>
    </div>
  );
}
